import { PlusOutlined } from "@ant-design/icons";
import { Button, Table, Tag } from "antd";
import { ConsentTag } from "components/consentTag";
import { DATE_TIME_FORMAT } from "common/constants";
import { toTitleCase } from "common/utils";
import { useUserType } from "features/auth";
import { useGetDebtorCommunicationHistoryQuery } from "features/communicationsTable/agencyPortal/communicationsTableAPI";
import CommunicationModal from "features/communicationsTable/components/communicationModal";
import NotesModal from "features/communicationsTable/components/notesModal";
import { useGetCreditorPortalDebtorCommunicationHistoryQuery } from "features/communicationsTable/creditorPortal/communicationsTableAPI";
import { useFetchDebtorAccountsQuery } from "features/debtorAccountsTable/agencyPortal/debtorAccountsAPI";
import { useFetchDebtorContactsQuery } from "features/debtorSider/agencyPortal/debtorSiderAPI";
import { useFetchCreditorPortalDebtorContactsQuery } from "features/debtorSider/creditorPortal/debtorSiderAPI";
import { useFetchCreditorPortalDebtorAccountsQuery } from "features/debtorAccountsTable/creditorPortal/debtorAccountsAPI";
import {
  useFetchBackendConstantsQuery,
  useFetchCollectorsQuery,
} from "features/home/agencyPortal/homeAPI";
import { useFetchCommunicationResultItemsQuery } from "features/communicationResultItems/communicationResultItemsAPI";
import { useFetchDebtorPacketedDebtorsQuery } from "features/debtorProfile/debtorProfileAPI";
import { PERMISSIONS, useAuthorization } from "features/permissions";
import moment from "moment-timezone";
import { useMemo, useState } from "react";
import styled from "styled-components";

const StyledTag = styled(Tag)`
  margin: 4px 4px;
`;

const StyledTable = styled(Table)`
  cursor: pointer;
  margin-top: 12px;
  & .ant-table-cell:not(th) {
    padding: 4px 8px !important;
  }
`;

const StyledButton = styled(Button)`
  padding: 0;
`;

const StyledSpan = styled.span`
  white-space: pre-wrap;
`;

function CommunicationsTable({ debtorId, hasConsumerConsent = false, isDebtorPacketed = false }) {
  const { isAgencyUserType } = useUserType();
  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: allResultItems = [] } = useFetchCommunicationResultItemsQuery({});
  const [selectedRecord, setSelectedRecord] = useState(null);

  // Build a lookup map from slug to name for all result types
  const resultSlugToNameMap = useMemo(() => {
    return allResultItems.reduce((acc, item) => {
      acc[item.slug] = item.name;
      return acc;
    }, {});
  }, [allResultItems]);

  const contactMethodToContactMethodDisplayMap = useMemo(() => {
    if (!constants || !constants.contactMethods) {
      return {};
    }
    return constants.contactMethods.reduce((acc, contactMethod) => {
      acc[contactMethod.value] = contactMethod.display;
      return acc;
    }, {});
  }, [constants]);

  const [isAddCommunicationsModalVisible, setIsAddCommunicationsModalVisible] = useState(false);
  const { data: agencyCommunicationsTableData, isLoading: isAgencyCommunicationsQueryLoading } =
    useGetDebtorCommunicationHistoryQuery(
      {
        debtorId,
        isIncludePacketed: true,
      },
      { skip: !isAgencyUserType },
    );
  const {
    data: creditorPortalCommunicationsTableData,
    isLoading: isCreditorPortalCommunicationsLoading,
  } = useGetCreditorPortalDebtorCommunicationHistoryQuery(
    {
      debtorId,
    },
    { skip: isAgencyUserType },
  );

  const [communicationsTableData, isLoading] = isAgencyUserType
    ? [agencyCommunicationsTableData, isAgencyCommunicationsQueryLoading]
    : [creditorPortalCommunicationsTableData, isCreditorPortalCommunicationsLoading];

  // We don't show packeted comm logs for creditor portal users currently, so there is no need
  // to get debtor packeted debtors for creditor portal users
  const { data: debtorPacketedDebtors } = useFetchDebtorPacketedDebtorsQuery(
    {
      debtorId,
    },
    { skip: !isAgencyUserType || !isDebtorPacketed },
  );

  const { data: agencyDebtorContacts } = useFetchDebtorContactsQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const { data: creditorPortalDebtorContacts } = useFetchCreditorPortalDebtorContactsQuery(
    { debtorId },
    { skip: isAgencyUserType },
  );
  const debtorContacts = isAgencyUserType ? agencyDebtorContacts : creditorPortalDebtorContacts;

  // Fetch debtor accounts for mapping internal IDs to external IDs
  const { data: agencyDebtorAccounts } = useFetchDebtorAccountsQuery(
    { debtorId },
    { skip: !isAgencyUserType },
  );
  const { data: creditorPortalDebtorAccounts } = useFetchCreditorPortalDebtorAccountsQuery(
    { debtorId },
    { skip: isAgencyUserType },
  );
  const debtorAccounts = isAgencyUserType ? agencyDebtorAccounts : creditorPortalDebtorAccounts;

  // Create mapping from internal account ID to external account ID
  const accountIdToExternalIdMap = useMemo(() => {
    if (!debtorAccounts) {
      return {};
    }
    return debtorAccounts.reduce((acc, account) => {
      acc[account.id] = account.externalId;
      return acc;
    }, {});
  }, [debtorAccounts]);

  const { data: collectors } = useFetchCollectorsQuery({}, { skip: !isAgencyUserType });
  const { data: isCreateCommunicationAuthorized } = useAuthorization(
    PERMISSIONS.COMMUNICATION__CREATE,
  );
  const { data: isCommunicationGetNotesAuthorized } = useAuthorization(
    PERMISSIONS.COMMUNICATION__GET_NOTES,
  );

  const handleAddCommunicationsOk = async () => {
    setIsAddCommunicationsModalVisible(false);
  };

  const handleAddCommunicationsCancel = () => {
    setIsAddCommunicationsModalVisible(false);
  };

  const onNotesModalClose = () => {
    setSelectedRecord(null);
  };

  // Build filters from actual dataset results
  const resultFilters = useMemo(() => {
    if (!communicationsTableData) {
      return [];
    }

    // Collect all unique result values from the dataset
    const uniqueResultValues = new Set();
    communicationsTableData.forEach((communication) => {
      [
        ...(communication.callResults ?? []),
        ...(communication.mailResults ?? []),
        ...(communication.textResults ?? []),
        ...(communication.communicationResults ?? []),
        ...(communication.voicemailResults ?? []),
      ].forEach((result) => {
        // result can be either a string value or an object with value property
        const value = typeof result === "string" ? result : result.value;
        if (value) {
          uniqueResultValues.add(value);
        }
      });
    });

    // Map values to display names using resultSlugToNameMap
    const filters = [];
    uniqueResultValues.forEach((value) => {
      const display = resultSlugToNameMap[value] || value;
      filters.push({
        text: display,
        value,
      });
    });

    return filters;
  }, [communicationsTableData, resultSlugToNameMap]);

  const columns = [
    {
      title: "Start Time",
      dataIndex: "communicationStartTime",
      key: "communicationStartTime",
      render: (text, _) => (text ? moment(text).format(DATE_TIME_FORMAT) : "-"),
      sorter: (a, b) =>
        moment(a.communicationStartTime).unix() - moment(b.communicationStartTime).unix(),
    },
    {
      title: "Recipient",
      key: "Recipient",
      render: (text, record) => {
        // Note that record.debtor and record.debtorContact are IDs, not objects
        const isPacketed = record.debtor.toString() !== debtorId.toString();

        if (!isPacketed) {
          if (record.contactType === "debtor_contact") {
            const debtorContact = debtorContacts?.find(
              (contact) => contact.id === record.debtorContact,
            );
            if (debtorContact) {
              const fullName = [
                debtorContact.firstName,
                debtorContact.middleName,
                debtorContact.lastName,
              ]
                .filter((x) => x)
                .join(" ");
              return `${fullName} (${debtorContact.relationshipToDebtor})`;
            }
          }

          return toTitleCase({ str: record.contactType, delimiter: "_" });
        }

        // For packeted debtors, display only the debtor name with a tag (no contact type or contact name).
        // Full details of the actual contacted person are available in the debtor's own comm log (see above logic).
        const packetedDebtor = debtorPacketedDebtors?.results?.find(
          (d) => d.id.toString() === record.debtor.toString(),
        );
        if (!packetedDebtor) {
          return null;
        }
        return (
          <>
            <span>{packetedDebtor.name}</span> <Tag color="blue">PACKETED</Tag>
          </>
        );
      },
      sorter: (a, b) => {
        return a.recipient?.toLowerCase() > b.recipient?.toLowerCase();
      },
    },
    {
      title: "Contact Method",
      dataIndex: "contactMethod",
      key: "contactMethod",
      render: (contactMethod, record) => {
        let contactMethodDisplay = contactMethodToContactMethodDisplayMap[contactMethod];
        if (!contactMethodDisplay) {
          contactMethodDisplay = contactMethod;
        }
        const hasPhoneNumber =
          ["phone", "text", "voicemail", "ai_dialer"].includes(contactMethod.toLowerCase()) &&
          record.phoneNumber;

        let content = hasPhoneNumber
          ? `${contactMethodDisplay} (${record.phoneNumber})`
          : contactMethodDisplay;
        const communicationOrigin = record?.communicationOrigin;

        content = communicationOrigin
          ? `${content} (${toTitleCase({ str: communicationOrigin })})`
          : content;
        return (
          <>
            <span>{content}</span>
            <br />
          </>
        );
      },
      sorter: {
        compare: (a, b) => {
          const contactMethodA = a?.contactMethod?.toUpperCase();
          const contactMethodB = b?.contactMethod?.toUpperCase();
          if (contactMethodA < contactMethodB) {
            return -1;
          }
          if (contactMethodA > contactMethodB) {
            return 1;
          }

          return 0;
        },
      },
    },
    {
      title: "Affected Accounts (#)",
      dataIndex: "accountIds",
      key: "accountIds",
      render: (accountIds, _) => {
        if (!accountIds?.length) {
          return "-";
        }

        // Map internal account IDs to external IDs
        const externalIds = accountIds
          .map((accountId) => accountIdToExternalIdMap[accountId])
          .filter(Boolean); // Filter out any undefined values

        return externalIds.length > 0 ? externalIds.join(", ") : "-";
      },
    },
    {
      title: "Results",
      width: 400,
      // filters based on current communication result tags from actual dataset
      filters: resultFilters,
      filterMode: "tree",
      filterSearch: true,
      onFilter: (value, record) => {
        const getResultValue = (result) => (typeof result === "string" ? result : result?.value);
        return (
          (record.callResults ?? []).some((result) => getResultValue(result) === value) ||
          (record.mailResults ?? []).some((result) => getResultValue(result) === value) ||
          (record.textResults ?? []).some((result) => getResultValue(result) === value) ||
          (record.communicationResults ?? []).some((result) => getResultValue(result) === value) ||
          (record.voicemailResults ?? []).some((result) => getResultValue(result) === value)
        );
      },
      render: (_, communication) => {
        const callResults = communication.callResults ?? [];
        const mailResults = communication.mailResults ?? [];
        const textResults = communication.textResults ?? [];
        const communicationResults = communication.communicationResults ?? [];
        const voicemailResults = communication.voicemailResults ?? [];
        return (
          <div>
            {communicationResults.map((result) => (
              <StyledTag color="#2db7f5" key={result}>
                {resultSlugToNameMap[result] || result}
              </StyledTag>
            ))}
            {mailResults.map((result) => (
              <StyledTag color="#ebb25e" key={result}>
                {resultSlugToNameMap[result] || result}
              </StyledTag>
            ))}
            {callResults.map((result) => (
              <StyledTag color="#2fba5b" key={result}>
                {resultSlugToNameMap[result] || result}
              </StyledTag>
            ))}
            {voicemailResults.map((result) => (
              <StyledTag color="#2fba5b" key={result}>
                {resultSlugToNameMap[result] || result}
              </StyledTag>
            ))}
            {textResults.map((result) => {
              const display = resultSlugToNameMap[result];
              if (display) {
                return (
                  <StyledTag color="#2abcdc" key={result}>
                    {display}
                  </StyledTag>
                );
              }
              return null;
            })}
            {communication.isConsentForText != null && (
              <ConsentTag isConsent={communication.isConsentForText}>Text</ConsentTag>
            )}
            {communication.isConsentForEmail != null && (
              <ConsentTag isConsent={communication.isConsentForEmail}>Email</ConsentTag>
            )}
            {communication.isConsentForAiCalls != null && (
              <ConsentTag isConsent={communication.isConsentForAiCalls}>AI Calls</ConsentTag>
            )}
            {!!communication.isCeaseCommunication && (
              <StyledTag color="red">Cease Communication</StyledTag>
            )}
          </div>
        );
      },
      key: "Results",
    },
    {
      title: "Recorded At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text, _) => (text ? moment(text).format(DATE_TIME_FORMAT) : "-"),
      sorter: (a, b) =>
        moment(a.communicationStartTime).unix() - moment(b.communicationStartTime).unix(),
    },
    {
      title: "Recorded By",
      dataIndex: "contactedBy",
      key: "contactedBy",
      render: (text, _) => {
        const collector = collectors?.find((c) => c.id === text);
        return collector?.email ?? "";
      },
    },
    // {
    //   title: "Preferred Follow-Up Time",
    //   dataIndex: "preferredFollowUpTime",
    //   key: "preferredFollowUpTime",
    //   render: (text, record) =>
    //     record.preferredFollowUpTime
    //       ? moment(record.preferredFollowUpTime).format(DATE_TIME_FORMAT)
    //       : "-",
    //   sorter: (a, b) =>
    //     moment(a.preferredFollowUpTime).unix() - moment(b.preferredFollowUpTime).unix(),
    // },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      fixed: "right",
      width: 400,
      render: (text, record) => {
        const hasCallRecording = record.callRecordingUrl && record.callRecordingUrl !== "";
        const hasCallTranscription =
          record.callTranscription &&
          Array.isArray(record.callTranscription) &&
          record.callTranscription.length > 0;
        return (
          <StyledSpan>
            {text?.length > 200 || hasCallTranscription || hasCallRecording ? (
              <span>
                {text?.substring(0, 200)}...
                <StyledButton onClick={() => setSelectedRecord(record)} type="link">
                  See Details
                </StyledButton>
              </span>
            ) : (
              text
            )}
          </StyledSpan>
        );
      },
      hidden: !isCommunicationGetNotesAuthorized,
    },
  ].filter((column) => !column.hidden);

  return (
    <>
      <NotesModal
        title="Call Details"
        open={selectedRecord !== null}
        onClose={onNotesModalClose}
        record={selectedRecord}
      />

      <CommunicationModal
        debtorId={debtorId}
        title="Add Communication"
        open={isAddCommunicationsModalVisible}
        onOk={handleAddCommunicationsOk}
        onCancel={handleAddCommunicationsCancel}
      />
      {hasConsumerConsent && isCreateCommunicationAuthorized && (
        <StyledButton
          type="link"
          onClick={() => setIsAddCommunicationsModalVisible(true)}
          icon={<PlusOutlined />}
        >
          Add Communication Log
        </StyledButton>
      )}
      <StyledTable
        loading={isLoading}
        bordered
        // @ts-ignore
        columns={columns}
        dataSource={communicationsTableData}
        scroll={{ x: "max-content" }}
      />
    </>
  );
}

export default CommunicationsTable;
